import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/api';
import {
  HiHome,
  HiShoppingCart,
  HiCube,
  HiDocumentText,
  HiChartBar,
  HiCurrencyDollar,
  HiLogout,
  HiMenu,
  HiX
} from 'react-icons/hi';

const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on mobile when route changes
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    const apiBaseUrl = getApiBaseUrl();
    window.location.href = `${apiBaseUrl}/api/auth/logout`;
  };

  const menuItems = [
    {
      path: '/',
      label: 'Overview',
      icon: HiHome,
      description: 'Dashboard & quick stats'
    },
    {
      path: '/orders/retail',
      label: 'Retail Order',
      icon: HiShoppingCart,
      description: 'Create retail orders'
    },
    {
      path: '/orders/wholesale',
      label: 'Wholesale Order',
      icon: HiCube,
      description: 'Create wholesale orders'
    },
    {
      path: '/inventory',
      label: 'Inventory',
      icon: HiDocumentText,
      description: 'Manage products'
    },
    {
      path: '/orders',
      label: 'Order Management',
      icon: HiChartBar,
      description: 'View & manage orders'
    },
    {
      path: '/finance',
      label: 'Finance',
      icon: HiCurrencyDollar,
      description: 'Settlement reports'
    }
  ];

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For order-related routes, be more specific to avoid conflicts
    if (path === '/orders') {
      return location.pathname === '/orders';
    }
    if (path === '/orders/retail') {
      return location.pathname === '/orders/retail';
    }
    if (path === '/orders/wholesale') {
      return location.pathname === '/orders/wholesale';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col
        lg:relative lg:translate-x-0 lg:shadow-none lg:border-r lg:border-gray-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Ekdunt Arts"
              className="w-8 h-8 rounded"
            />
            <span className="text-lg font-semibold text-gray-900">Ekdunt Arts</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="mt-8 px-4">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                    ${isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'}`} />
                  <div className="flex-1">
                    <div className="font-medium">{item.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Logout Button */}
        <div className="mt-auto p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-700 transition-colors duration-200 group"
          >
            <HiLogout className="w-5 h-5 mr-3 text-gray-400 group-hover:text-red-500" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header for Mobile */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <HiMenu className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="Ekdunt Arts"
                className="w-6 h-6 rounded"
              />
              <span className="text-sm font-medium text-gray-900">Ekdunt Arts</span>
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
